<template>
  <div id="timing-model">
    <div class="report-loader text-center" v-if="loading">
      <img src="/images/loading.gif" />
    </div>
    <div v-else>
      <div
        :style="
          slug === 'indicator-signals' || preview === true
            ? ''
            : 'display:none;'
        "
      >
        <IndicatorPhasesComponent
          :companyName="companyName"
          :companyLogo="companyLogo"
          :phaseTable="phaseTable"
          :definitions="indicatorDefinitions.definitions"
          :consulting_slug="consulting_slug"
          :productType="productType"
          :phaseData="phaseData"
          :note="note"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        :style="
          slug === 'data-trends' || preview === true ? '' : 'display:none;'
        "
      >
        <DataTrendsComponent
          ref="dataTrends"
          :companyName="companyName"
          :companyLogo="companyLogo"
          :dataTrendsData="dataTrendsData"
          :consulting_slug="consulting_slug"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div :style="slug === 'roc' || preview === true ? '' : 'display:none;'">
        <RocComponent
          ref="roc"
          :rocData="rocData"
          :companyLogo="companyLogo"
          :companyName="companyName"
          :consulting_slug="consulting_slug"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        v-for="(division, i) in divisions"
        v-bind:key="'division' + i"
        :style="
          slug === 'division' + i || preview === true ? '' : 'display:none;'
        "
      >
        <DivisionComparisonComponent
          ref="division"
          :divisionData="division"
          :companyName="companyName"
          :companyLogo="companyLogo"
          :rowNumber="i"
          :chartID="'divisionChart' + i"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        v-for="(indicator, i) in indicators"
        v-bind:key="'indicator' + i"
        :style="
          slug === 'indicator' + i || preview === true ? '' : 'display:none;'
        "
      >
        <IndicatorCompareComponent
          ref="indicator"
          :indicatorData="indicator"
          :companyLogo="companyLogo"
          :rowNumber="i"
          :preview="preview"
          :consulting_slug="consulting_slug"
          :companyName="companyName"
          :chartID="'indicatorCompareChart' + i"
          :tableID="'indicatorDataTable' + i"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        :style="
          slug === 'timing-model-guide' || preview === true
            ? ''
            : 'display:none;'
        "
      >
        <TimingModelGuideComponent
          :companyLogo="companyLogo"
          :productType="productType"
          :productTypeShorthand="productTypeShorthand"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        :style="
          slug === 'terminology-methodology' || preview === true
            ? ''
            : 'display:none;'
        "
      >
        <TerminologyComponent
          :companyLogo="companyLogo"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        :style="
          slug === 'indicator-definitions' || preview === true
            ? ''
            : 'display:none;'
        "
      >
        <DefinitionOfIndicatorsComponent
          :companyLogo="companyLogo"
          :indicatorDefinitions="indicatorDefinitions"
          :reportTypeId="reportTypeId"
        />
      </div>

      <div
        :style="
          slug === 'management-objectives' || preview === true
            ? ''
            : 'display:none;'
        "
      >
        <ManagementObjectivesComponent
          :companyLogo="companyLogo"
          :reportTypeId="reportTypeId"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import DefinitionOfIndicatorsComponent from '~/components/report/modules/live-report/shared/DefinitionOfIndicatorsComponent.vue';
  import IndicatorPhasesComponent from '~/components/report/modules/live-report/shared/IndicatorPhasesComponent.vue';
  import DataTrendsComponent from '~/components/report/modules/live-report/shared/DataTrendsComponent.vue';
  import IndicatorCompareComponent from '~/components/report/modules/live-report/shared/IndicatorCompareComponent.vue';
  import ManagementObjectivesComponent from '~/components/report/modules/live-report/shared/ManagementObjectivesComponent.vue';
  import TerminologyComponent from '~/components/report/modules/live-report/shared/TerminologyComponent.vue';
  import TimingModelGuideComponent from '~/components/report/modules/live-report/shared/TimingModelGuideComponent.vue';
  import DivisionComparisonComponent from '~/components/report/modules/live-report/shared/DivisionComparisonComponent.vue';
  import RocComponent from '~/components/report/modules/live-report/shared/ROCComponent.vue';
  import pptxgen from 'pptxgenjs';
  import pdfMake from 'pdfmake/build/pdfmake';
  import pdfFonts from 'pdfmake/build/vfs_fonts';

  export default {
    name: 'TimingModelComponent',
    props: {
      slug: {
        type: String,
        default: '',
      },
      index: {
        type: [Number, String],
        default: '',
      },
      preview: {
        type: Boolean,
        default: false,
      },
      consulting_slug: {
        type: String,
        default: '',
      },
      apiResponse: {
        type: Object,
        default: () => ({}),
      },
      reportID: {
        type: [Number, String],
        default: '',
      },
      reportTypeId: {
        type: [Number, String],
        default: '',
      },
    },
    components: {
      DefinitionOfIndicatorsComponent,
      IndicatorPhasesComponent,
      DataTrendsComponent,
      IndicatorCompareComponent,
      ManagementObjectivesComponent,
      TerminologyComponent,
      TimingModelGuideComponent,
      DivisionComparisonComponent,
      RocComponent,
    },
    data() {
      return {
        loading: true,
        companyName: '',
        companyLogo: '',
        productType: '',
        phaseTable: [],
        phaseData: {},
        indicatorDefinitions: {
          evenRows: [],
          oddRows: [],
          definitions: [],
        },
        dataTrendsData: {},
        rocData: {},
        currentYear: new Date().getFullYear(),
        divisions: [],
        note: '',
        yearEnd: '',
        productTypeShorthand: '',
        indicators: [],
      };
    },
    mounted() {
      this.setData();
    },
    updated() {
      if (!this.preview) {
        if (this.slug === 'data-trends') {
          Plotly.Plots.resize('dataTrendsChart');
        } else if (this.slug === 'roc') Plotly.Plots.resize('rocChart');
        else if (this.slug === 'division' + this.index)
          Plotly.Plots.resize('divisionChart' + this.index);
        else if (this.slug === 'indicator' + this.index) {
          Plotly.Plots.resize('indicatorCompareChart' + this.index);
        }
      }
    },
    methods: {
      setData: function () {
        this.companyName = this.apiResponse.CompanyName ?? '';
        this.companyLogo = this.apiResponse.CompanyLogo ?? 'itrLogo';
        this.productType = this.apiResponse.CoverPage.ProductType ?? '';
        this.productTypeShorthand =
          this.apiResponse.CoverPage.ProductType ?? '';
        // if updated in the future set to this.productTypeShorthand = this.reportInfo.ProductTypeShorthand ?? '';
        var currentYear = new Date().getFullYear().toString().substr(2, 2);
        var lastYear = (new Date().getFullYear() - 1).toString().substr(2, 2);

        this.note =
          'The table above shows what each of the included indicators is signaling for your business based on the relevant timing relationship (lead/lag). Each of these indicators gives evidence regarding the likely trajectory of your business. For example, if US Industrial Production normally leads your business by 12 months and is expected to be in Phase D as of Dec-' +
          lastYear +
          ', then the table would show "D" in the Dec-' +
          currentYear +
          ' column.';

        //definition page
        this.indicatorDefinitions = {
          evenRows: [],
          oddRows: [],
          definitions:
            this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows,
        };
        //loop and set even and odds
        for (
          let i = 0;
          i <
          this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows
            .length;
          ++i
        ) {
          if (i % 2 === 0) {
            this.indicatorDefinitions.evenRows.push(
              this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows[i]
            );
          } else {
            this.indicatorDefinitions.oddRows.push(
              this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows[i]
            );
          }
        }

        //indicator phases
        this.phaseTable =
          this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable;
        this.phaseData = this.apiResponse.IndicatorSignalsPage;

        //data trends
        this.dataTrendsData = this.apiResponse.DataTrendsPage;

        //roc data
        this.rocData = this.apiResponse.RoCPage;

        //all indicators
        this.indicators = this.apiResponse.IndicatorPages;

        //division compare pages
        this.divisions = this.apiResponse.RoCDivPages;

        this.buildPageDropDownMenu();
      },
      buildPageDropDownMenu: function () {
        var pages = document.getElementById('report-page');
        pages.innerHTML = '';

        //map indicator signals
        var indicatorPhases = document.createElement('option');
        indicatorPhases.text = 'Indicator Signals';
        indicatorPhases.value = 'indicator-signals';
        indicatorPhases.selected =
          this.slug === 'indicator-signals' || this.slug === '';
        pages.add(indicatorPhases);

        //map data trends
        var dataTrends = document.createElement('option');
        dataTrends.text = 'Data Trends';
        dataTrends.value = 'data-trends';
        dataTrends.selected = this.slug === 'data-trends';
        pages.add(dataTrends);

        //map roc
        var roc = document.createElement('option');
        roc.text = 'Rates-of-Change';
        roc.value = 'roc';
        roc.selected = this.slug === 'roc';
        pages.add(roc);

        //map division
        if (this.apiResponse.RoCDivPages) {
          for (let i = 0; i < this.apiResponse.RoCDivPages.length; ++i) {
            var divPage = document.createElement('option');
            divPage.text =
              'Division Comparison - ' +
              this.apiResponse.RoCDivPages[i].OtherSeriesName;
            divPage.value = 'division' + i;
            divPage.selected = this.slug === 'division' + i;
            divPage.setAttribute('index', i);
            pages.add(divPage);
          }
        }

        //map indicators
        if (this.apiResponse.IndicatorPages?.length > 0) {
          for (let i = 0; i < this.apiResponse.IndicatorPages.length; ++i) {
            var indicator = document.createElement('option');
            indicator.text =
              this.apiResponse.IndicatorPages[i].IndicatorSeriesName;
            indicator.value = 'indicator' + i;
            indicator.selected = this.slug === 'indicator' + i;
            indicator.setAttribute('index', i);
            pages.add(indicator);
          }
        }

        //Timing Model Guide
        var timingModelGuide = document.createElement('option');
        timingModelGuide.text = 'Timing Model Guide';
        timingModelGuide.value = 'timing-model-guide';
        timingModelGuide.selected = this.slug === 'timing-model-guide';
        pages.add(timingModelGuide);

        //terminology
        var terminology = document.createElement('option');
        terminology.text = 'Terminology and Methodology';
        terminology.value = 'terminology-methodology';
        terminology.selected = this.slug === 'terminology-methodology';
        pages.add(terminology);

        //map definitions
        var definitions = document.createElement('option');
        definitions.text = 'Indicator Definitions';
        definitions.value = 'indicator-definitions';
        definitions.selected = this.slug === 'indicator-definitions';
        pages.add(definitions);

        //management objective
        var management = document.createElement('option');
        management.text = 'Management Objectives';
        management.value = 'management-objectives';
        management.selected = this.slug === 'management-objectives';
        pages.add(management);

        if (!this.preview) {
          //handle paginations
          document.getElementById('paginationPagesLabel').innerHTML =
            pages.selectedIndex + 1 + '/' + pages.length;
        }

        this.loading = false;
      },
      async getReportLogo() {
        let companyLogoImage = await this.getOnlyCompanyLogo();

        if (!companyLogoImage) {
          companyLogoImage = this.getBase64Image(
            document.getElementById('itrLogo')
          );
        }

        return companyLogoImage;
      },
      async getOnlyCompanyLogo() {
        let companyLogoImage = null;

        try {
          const { data } = await axios.get(
            `/company-logo/report/${this.reportID}/image-data-url`
          );
          companyLogoImage = data;
        } catch (error) {
          //
        }

        return companyLogoImage;
      },
      async downloadPDF() {
        pdfMake.vfs = pdfFonts;

        let companyLogoImageDataUrl = await this.getOnlyCompanyLogo();
        if (!companyLogoImageDataUrl) {
          companyLogoImageDataUrl = 'itrLogo';
        }

        for (let i = 0; i < this.apiResponse.IndicatorPages.length; ++i) {
          var chartTitleFontSize = 30;
          if (this.apiResponse.IndicatorPages[i].ChartTitle.length >= 70) {
            chartTitleFontSize = 10;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 40
          ) {
            chartTitleFontSize = 12;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 20
          ) {
            chartTitleFontSize = 18;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 15
          ) {
            chartTitleFontSize = 25;
          }
        }

        //create cover page chart
        await this.createCoverPageChart();

        //create cover page
        var coverPage = [
          {
            // width: 150,
            // height: 50,
            fit: [200, 200],
            alignment: 'center',
            image: 'itrLogo',
            margin: [0, 0, 0, 15],
          },
          {
            text:
              this.apiResponse.CoverPage.ProductType +
              document.getElementById('tradeMark').innerHTML,
            fontSize: 62,
            alignment: 'center',
            bold: true,
            italics: true,
            style: 'darkBlue',
            margin: [0, 0, 0, 15],
          },
          {
            text: [
              '=====================================================     ',
              {
                text: this.apiResponse.CoverPage.ReportDate,
              },
              '     ==========',
            ],
            style: 'darkBlue',
            alignment: 'center',
            margin: [0, 0, 0, 15],
          },
          {
            text: this.apiResponse.CompanyName,
            style: 'darkBlue',
            bold: true,
            alignment: 'center',
            fontSize: 14,
          },
          {
            image: await Plotly.toImage('coverPageChart', {
              format: 'png',
              width: 800,
              height: 600,
            }),
            height: 150,
            width: 300,
            alignment: 'center',
          },
          {
            margin: [200, 20, 0, 15],
            alignment: 'center',
            columnGap: 5,
            columns: [
              {
                image: 'phaseAFull',
                height: 50,
                width: 85,
                alignment: 'center',
              },
              {
                image: 'phaseBFull',
                height: 50,
                width: 85,
                alignment: 'center',
              },
              {
                image: 'phaseCFull',
                height: 50,
                width: 85,
                alignment: 'center',
              },
              {
                image: 'phaseDFull',
                height: 50,
                width: 85,
                alignment: 'center',
              },
            ],
          },
          {
            bold: true,
            style: 'darkBlue',
            text: '603.796.2500 | reports@itreconomics.com',
            alignment: 'center',
          },
          {
            bold: true,
            style: 'darkBlue',
            text: 'www.itrondemand.com',
            link: 'https://www.itrondemand.com',
            alignment: 'center',
          },
        ];

        {
          //start indicator phases
          var indicatorPhases = [
            {
              pageBreak: 'before',
              // columnGap: 5,
              columns: [
                {
                  // width: 50,
                  // height: 20,
                  fit: [100, 100],
                  image: 'itrLogo',
                },
                {
                  width: '65%',
                  alignment: 'center',
                  fontSize: 14,
                  bold: true,
                  text: 'Indicator Signals for ' + this.apiResponse.CompanyName,
                },
                {
                  // width: 50,
                  // height: 20,
                  fit: [100, 100],
                  alignment: 'right',
                  image: companyLogoImageDataUrl,
                },
              ],
            },
            {
              margin: [0, 10, 0, 10],
              table: {
                headerRows: 1,
                widths: ['*'],
                body: [
                  [
                    {
                      text: '|',
                      fillColor: '#3075A4',
                      color: '#3075A4',
                      fontSize: 14,
                      alignment: 'center',
                    },
                  ],
                ],
              },
              layout: {
                hLineColor: function () {
                  return '#3075A4';
                },
                vLineColor: function () {
                  return '#3075A4';
                },
              },
            },
          ];

          //create table
          var phaseTable = {
            layout: 'lightHorizontalLines',
            fontSize: 10,
            margin: [0, 0, 0, 10],
            table: {
              headerRows: 1,
              widths: [],
              body: [],
            },
          };

          //push table widths and build headers
          let phaseTableHeaders = [];
          for (
            let i = 0;
            i <
            this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Headers
              .length;
            ++i
          ) {
            if (i === 0) phaseTable.table.widths.push('*');
            else phaseTable.table.widths.push('auto');

            let row = {
              text: this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                .Headers[i],
              alignment: i === 0 ? '' : 'center',
              bold: true,
            };
            phaseTableHeaders.push(row);
          }

          //add table data
          phaseTable.table.body.push(phaseTableHeaders);
          // phaseTable.table.body.push(this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Headers);

          //add row data
          for (
            let i = 0;
            i <
            this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Rows
              .length;
            ++i
          ) {
            var row = [
              this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Rows[
                i
              ].SeriesName,
            ];
            for (
              let x = 0;
              x <
              this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Rows[
                i
              ].PhaseList.length;
              ++x
            ) {
              const phase =
                this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                  .Rows[i].PhaseList[x];
              row.push({
                text: phase,
                alignment: 'center',
                style:
                  phase === 'A'
                    ? 'phaseA'
                    : phase === 'B'
                      ? 'phaseB'
                      : phase === 'C'
                        ? 'phaseC'
                        : phase === 'D'
                          ? 'phaseD'
                          : 'defaultTableTextColor',
              });
            }
            row.push({
              text: this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                .Rows[i].Timing,
              alignment: 'center',
              style: 'defaultTableTextColor',
            });
            phaseTable.table.body.push(row);
          }

          //add table to indicatorPhases
          indicatorPhases.push(phaseTable);

          //add notes area
          let phaseNotesText = [
            { text: 'Note:', bold: true, fontSize: 8 },
            {
              text: this.note,
              fontSize: 8,
            },
          ];

          indicatorPhases.push(phaseNotesText);
        } //end indicator phases

        {
          //start data trends
          var dataTrends = [
            {
              pageBreak: 'before',
              text:
                this.apiResponse.DataTrendsPage.CompanyName +
                ' - ' +
                this.apiResponse.DataTrendsPage.DataType,
              alignment: 'center',
              fontSize: 14,
              bold: true,
            },
            {
              text: 'Data Trends, ' + this.apiResponse.DataTrendsPage.DataLabel,
              alignment: 'center',
              fontSize: 10,
              margin: [0, 0, 0, 0],
            },
          ];

          //add chart image
          dataTrends.push({
            columnGap: 10,
            columns: [
              {
                // image: await this.$refs.dataTrends.pushToImage(800, 500),
                image: $('#dataTrendsChartImg').attr('src'),
                width: 600,
                height: 400,
              },
              [
                {
                  text: this.apiResponse.DataTrendsPage.Last3MMLabel,
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 10, 0, 10],
                },
                {
                  text: this.apiResponse.DataTrendsPage.Last3MM,
                  alignment: 'center',
                  color: '#65A9D2',
                  fontSize: 16,
                  bold: true,
                  margin: [0, 0, 0, 20],
                },
                {
                  text: this.apiResponse.DataTrendsPage.Last12MMLabel,
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 0, 0, 10],
                },
                {
                  text: this.apiResponse.DataTrendsPage.Last12MM,
                  alignment: 'center',
                  color: '#183961',
                  fontSize: 16,
                  bold: true,
                  margin: [0, 0, 0, 30],
                },
                {
                  text: 'OUTLOOK',
                  fontSize: 14,
                  alignment: 'center',
                  bold: true,
                  margin: [0, 0, 0, 5],
                },
                {
                  text: this.apiResponse.DataTrendsPage.Outlook,
                  fontSize: 12,
                  alignment: 'center',
                },
              ],
            ],
          });
        } //end data trends

        {
          //start roc
          var roc = [
            {
              pageBreak: 'before',
              text:
                this.apiResponse.CompanyName +
                ' - ' +
                this.apiResponse.RoCPage.DataType,
              alignment: 'center',
              fontSize: 14,
              bold: true,
            },
            {
              text: 'Rates-of-Change',
              alignment: 'center',
              fontSize: 10,
              margin: [0, 0, 0, 5],
            },
          ];

          //add chart image
          roc.push({
            columnGap: 10,
            columns: [
              {
                // image: await this.$refs.roc.pushToImage(800, 500),
                image: $('#rocChartImg').attr('src'),
                width: 600,
                height: 400,
              },
              [
                {
                  width: 190,
                  height: 100,
                  image:
                    this.apiResponse.RoCPage.Phase === 'A'
                      ? 'pdfphaseAFrame'
                      : this.apiResponse.RoCPage.Phase === 'B'
                        ? 'pdfphaseBFrame'
                        : this.apiResponse.RoCPage.Phase === 'C'
                          ? 'pdfphaseCFrame'
                          : this.apiResponse.RoCPage.Phase === 'D'
                            ? 'pdfphaseDFrame'
                            : 'phaseNAFrame',
                  alignment: 'center',
                  margin: [0, 0, 0, 20],
                },
                {
                  text: 'Quarterly Growth (3/12)',
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 0, 0, 10],
                },
                {
                  text: this.apiResponse.RoCPage.Last312,
                  alignment: 'center',
                  color: '#65A9D2',
                  bold: true,
                  fontSize: 16,
                  margin: [0, 0, 0, 20],
                },
                {
                  text: 'Annual Growth (12/12)',
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 0, 0, 10],
                },
                {
                  text: this.apiResponse.RoCPage.Last1212,
                  alignment: 'center',
                  color: '#183961',
                  bold: true,
                  fontSize: 16,
                  margin: [0, 0, 0, 20],
                },
                {
                  text: this.apiResponse.RoCPage.FirstTurningPointLabel,
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 0, 0, 10],
                },
                {
                  text: this.apiResponse.RoCPage.FirstTurningPointQ,
                  alignment: 'center',
                  bold: true,
                  fontSize: 16,
                  margin: [0, 0, 0, 20],
                },
                {
                  text: this.apiResponse.RoCPage.SecondTurningPointLabel,
                  alignment: 'center',
                  fontSize: 14,
                  margin: [0, 0, 0, 10],
                },
                {
                  text: this.apiResponse.RoCPage.SecondTurningPointQ,
                  alignment: 'center',
                  bold: true,
                  fontSize: 16,
                  // margin: [0, 0, 0, 10]
                },
              ],
            ],
          });
        } //end roc

        {
          //start divisions
          var divisions = [];
          for (let i = 0; i < this.apiResponse.RoCDivPages.length; ++i) {
            var division = [
              {
                pageBreak: 'before',
                text: this.apiResponse.RoCDivPages[i].PageTitle,
                alignment: 'center',
                fontSize: 14,
                bold: true,
              },
              {
                text:
                  this.apiResponse.RoCDivPages[i].OtherMetric +
                  ' Rates-of-Change',
                alignment: 'center',
                fontSize: 10,
                margin: [0, 0, 0, 0],
              },
            ];

            var divData = {
              columnGap: 10,
              columns: [
                {
                  // image: await this.$refs.division[i].pushToImage(800, 500),
                  image: $('#divisionChart' + i + 'ChartImg').attr('src'),
                  width: 600,
                  height: 400,
                },
                [
                  {
                    text: this.apiResponse.RoCDivPages[i].OtherSeriesName,
                    alignment: 'center',
                    margin: [0, 0, 0, 5],
                  },
                  {
                    width: 180,
                    // height: 100,
                    alignment: 'center',
                    margin: [0, 0, 0, 30],
                    image:
                      this.apiResponse.RoCDivPages[i].OtherPhase === 'A'
                        ? 'pdfphaseAFrame'
                        : this.apiResponse.RoCDivPages[i].OtherPhase === 'B'
                          ? 'pdfphaseBFrame'
                          : this.apiResponse.RoCDivPages[i].OtherPhase === 'C'
                            ? 'pdfphaseCFrame'
                            : this.apiResponse.RoCDivPages[i].OtherPhase === 'D'
                              ? 'pdfphaseDFrame'
                              : 'phaseNAFrame',
                  },
                  {
                    text: this.apiResponse.RoCDivPages[i].SubjectLabel,
                    alignment: 'center',
                    fontSize: 14,
                    margin: [0, 0, 0, 10],
                  },
                  {
                    text: this.apiResponse.RoCDivPages[i].SubjectLastData,
                    alignment: 'center',
                    color: '#183961',
                    bold: true,
                    fontSize: 16,
                    margin: [0, 0, 0, 30],
                  },
                  {
                    text: this.apiResponse.RoCDivPages[i].OtherLabel,
                    alignment: 'center',
                    fontSize: 14,
                    margin: [0, 0, 0, 10],
                  },
                  {
                    text: this.apiResponse.RoCDivPages[i].OtherLastData,
                    alignment: 'center',
                    color: '#EB6F2D',
                    bold: true,
                    fontSize: 16,
                    margin: [0, 0, 0, 30],
                  },
                ],
              ],
            };

            //add chart image
            division.push(divData);

            divisions.push(division);
          }
        } //end divisions

        {
          //start indicators
          var indicators = [];
          for (let i = 0; i < this.apiResponse.IndicatorPages.length; ++i) {
            var indicatorNotesTable = {
              table: {
                widths: ['*'],
                body: [],
              },
              layout: {
                hLineWidth: function (i, node) {
                  var notesLenght = node.table.body.length;
                  return i === notesLenght ? 1 : 0;
                },
                vLineWidth: function (i, node) {
                  return 0;
                },
                paddingLeft: function (i, node) {
                  return 30;
                },
                paddingTop: function (i, node) {
                  return 0;
                },
                paddingBottom: function (i, node) {
                  return 1;
                },
              },
            };

            var indicatorNotes = [];

            for (
              var x = 0;
              x < this.apiResponse.IndicatorPages[i].ExtraNotes.length;
              x++
            ) {
              if (
                this.apiResponse.IndicatorPages[i].ExtraNotes[x].includes(
                  '<a href='
                )
              )
                continue;

              indicatorNotes.push([
                {
                  text: [
                    this.apiResponse.IndicatorPages[i].ExtraNotes.length > 0
                      ? { text: 'Note: ', bold: true }
                      : '',
                    this.apiResponse.IndicatorPages[i].ExtraNotes.length > 0
                      ? this.apiResponse.IndicatorPages[i].ExtraNotes[x]
                      : '',
                  ],
                  fontSize: 8,
                },
              ]);
            }

            indicatorNotes.push([
              {
                text: [
                  this.apiResponse.IndicatorPages[i].OnDemandLink
                    ? 'Complete Indicator Analysis: '
                    : '',
                  {
                    text: this.apiResponse.IndicatorPages[i].OnDemandLink
                      ? 'ITR onDemand'
                      : '',
                    link: this.apiResponse.IndicatorPages[i].OnDemandLink,
                    bold: true,
                    decoration: 'underline',
                    color: '#0000ff',
                  },
                ],
                fontSize: 8,
              },
            ]);

            indicatorNotesTable.table.body = indicatorNotes;

            var indicator = [
              {
                pageBreak: 'before',
                table: {
                  widths: ['*'],
                  header: 1,
                  alignment: 'center',
                  body: [
                    [
                      {
                        text: this.apiResponse.IndicatorPages[i].Headline,
                        fillColor:
                          this.preview &&
                          this.apiResponse.IndicatorPages[i].HeadlineIsProofed
                            ? 'yellow'
                            : '',
                        alignment: 'center',
                        fontSize: 12,
                        bold: true,
                      },
                    ],
                  ],
                },
                layout: {
                  hLineColor: function () {
                    return '#09A684';
                  },
                  vLineColor: function () {
                    return '#09A684';
                  },
                  paddingLeft: function () {
                    return 20;
                  },
                  paddingRight: function () {
                    return 20;
                  },
                  paddingTop: function () {
                    return 5;
                  },
                  paddingBottom: function () {
                    return 5;
                  },
                },
                margin: [0, -35, 0, 0],
              },
              {
                text: this.apiResponse.IndicatorPages[i].ChartTitle,
                fontSize: chartTitleFontSize,
                alignment: 'center',
                margin: [0, 0, 0, 0],
              },
              {
                // image: await this.$refs.indicator[i].pushToImage(800, 500),
                image: $('#indicatorCompareChart' + i + 'ChartImg').attr('src'),
                width: 600,
                height: 325,
                alignment: 'center',
                margin: [0, 0, 0, 0],
              },
              indicatorNotesTable,
            ];

            var indicatorTable = {
              layout: 'lightHorizontalLines',
              fontSize: 10,
              margin: [-60, 0, 0, 10],
              table: {
                headerRows: 1,
                widths: [],
                body: [],
              },
            };

            //push table widths and build headers
            let indicatorTableHeaders = [];
            for (
              let x = 0;
              x <
              this.apiResponse.IndicatorPages[i].IndicatorPhaseTable.Columns
                .length;
              ++x
            ) {
              indicatorTable.table.widths.push('*');

              let row = {
                text: this.apiResponse.IndicatorPages[i].IndicatorPhaseTable
                  .Columns[x].MonthYear,
                fontSize:
                  this.apiResponse.IndicatorPages[i].IndicatorPhaseTable.Columns
                    .length > 6
                    ? 6.5
                    : 8,
                alignment: 'center',
              };
              indicatorTableHeaders.push(row);
            }

            //add table data
            indicatorTable.table.body.push(indicatorTableHeaders);

            //add row data
            var indicatorTableData = [];
            for (
              let x = 0;
              x <
              this.apiResponse.IndicatorPages[i].IndicatorPhaseTable.Columns
                .length;
              ++x
            ) {
              const phase =
                this.apiResponse.IndicatorPages[i].IndicatorPhaseTable.Columns[
                  x
                ].Phase;
              indicatorTableData.push({
                text: phase,
                alignment: 'center',
                style:
                  phase === 'A'
                    ? 'phaseA'
                    : phase === 'B'
                      ? 'phaseB'
                      : phase === 'C'
                        ? 'phaseC'
                        : phase === 'D'
                          ? 'phaseD'
                          : 'defaultTableTextColor',
              });
            }

            indicatorTable.table.body.push(indicatorTableData);

            // add chart image
            indicator.push({
              columns: [
                [
                  {
                    // fit: [100, 100],
                    width: 150,
                    // height: 100,
                    // alignment: 'center',
                    margin: [0, 5, 0, 0],
                    image:
                      this.apiResponse.IndicatorPages[i].IndicatorPhase ===
                      'Phase A'
                        ? 'phaseAFrame'
                        : this.apiResponse.IndicatorPages[i].IndicatorPhase ===
                            'Phase B'
                          ? 'phaseBFrame'
                          : this.apiResponse.IndicatorPages[i]
                                .IndicatorPhase === 'Phase C'
                            ? 'phaseCFrame'
                            : this.apiResponse.IndicatorPages[i]
                                  .IndicatorPhase === 'Phase D'
                              ? 'phaseDFrame'
                              : 'phaseNAFrame',
                  },
                  {
                    text: this.apiResponse.IndicatorPages[i].LeadLagLabel,
                    // alignment: 'center',
                    fontSize: 10,
                    margin: [0, 0, 80, 5],
                    width: 'auto',
                  },
                  {
                    text: this.apiResponse.IndicatorPages[i].CorrelLabel,
                    // alignment: 'center',
                    fontSize: 10,
                    margin: [0, 0, 80, 5],
                    width: 'auto',
                  },
                ],
                [
                  {
                    text: 'Turning Point Analysis',
                    fontSize: 10,
                    bold: true,
                    margin: [-60, 5, 0, 5],
                    width: 'auto',
                  },
                  {
                    width: 'auto',
                    ul: [
                      {
                        text: this.apiResponse.IndicatorPages[i].TPSentenceOne,
                        fontSize: 10,
                        margin: [-60, 0, 0, 5],
                      },
                      {
                        text: this.apiResponse.IndicatorPages[i].TPSentenceTwo,
                        fontSize: 10,
                        margin: [-60, 0, 0, 10],
                      },
                    ],
                  },
                  indicatorTable,
                ],
                [
                  {
                    text: 'Current Indicator Amplitude',
                    fontSize: 10,
                    bold: true,
                    margin: [10, 5, 0, 5],
                    width: 'auto',
                  },
                  {
                    text: this.apiResponse.IndicatorPages[i]
                      .IndicatorCurrentAmp,
                    fontSize: 10,
                    margin: [10, 0, 20, 5],
                    width: 'auto',
                  },
                  // {
                  //     text: 'Indicator Relevance',
                  //     fontSize: 10,
                  //     bold: true,
                  //     margin: [20, 10, 0, 5]
                  // },
                  // {
                  //     text: this.apiResponse.IndicatorPages[i].IndicatorRelevance,
                  //     fontSize: 10,
                  //     margin: [20, 0, 0, 5]
                  // },
                ],
              ],
            });

            indicators.push(indicator);
          }
        } //end indicators

        //guide
        var pdfGuide = [
          {
            pageBreak: 'before',
            image: 'guidePage',
            width: 850,
            alignment: 'center',
            margin: [0, -100, 0, -50],
          },
        ];

        // terminology and methodology
        var termAndMethodology = [
          {
            pageBreak: 'before',
            image: 'termMethodology',
            width: 850,
            alignment: 'center',
            margin: [0, -100, 0, -50],
          },
        ];

        //indicator def
        var indicatorDefinitions = [
          {
            pageBreak: 'before',
            table: {
              widths: ['*'],
              header: 1,
              body: [
                [
                  {
                    text: 'Indicator Definitions',
                    color: '#FFF',
                    fillColor: '#3075A4',
                    alignment: 'center',
                    fontSize: 14,
                    bold: true,
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: function () {
                return 1;
              },
              vLineWidth: function () {
                return 1;
              },
              hLineColor: function () {
                return '#3075A4';
              },
              vLineColor: function () {
                return '#3075A4';
              },
              paddingLeft: function () {
                return 40;
              },
              paddingRight: function () {
                return 40;
              },
              paddingTop: function () {
                return 5;
              },
              paddingBottom: function () {
                return 5;
              },
            },
            // fontSize: 14,
            // style: 'blueBarWText',
            // alignment: 'center'
          },
        ];

        var indicatorDefinitionsTable = {
          fontSize: 10,
          margin: [0, 0, 0, 10],
          table: {
            widths: ['*', '*'],
            body: [],
          },
          layout: {
            fillColor: function (rowIndex) {
              return rowIndex % 2 === 0 ? '#b8cce4' : null;
            },
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
          },
        };

        //loop through definitions and add
        for (
          let i = 0;
          i <
          this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows
            .length;
          ++i
        ) {
          var row =
            this.apiResponse.DefinitionsPage.IndicatorDefinitionsTable.Rows[i];

          indicatorDefinitionsTable.table.body.push([
            { text: row.Name, margin: [0, 5, 0, 5] },
            { text: row.Definition, margin: [0, 5, 0, 5] },
          ]);
        }

        indicatorDefinitions.push(indicatorDefinitionsTable);

        //managementObjectives
        var managementObjectives = [
          {
            pageBreak: 'before',
            image: 'managementObjectives',
            width: 850,
            alignment: 'center',
            margin: [0, -100, 0, -50],
          },
        ];

        //make pdf
        var docDefinition = {
          pageOrientation: 'landscape',
          info: {
            title: this.apiResponse.companyName,
          },
          content: [
            coverPage,
            indicatorPhases,
            dataTrends,
            roc,
            divisions,
            indicators,
            pdfGuide,
            termAndMethodology,
            indicatorDefinitions,
            managementObjectives,
          ],
          footer: (currentPage) => {
            return {
              margin: [40, 20, 40, 0],
              columns: [
                {
                  text:
                    (currentPage === 1
                      ? document.getElementById('copyright').innerHTML +
                        this.currentYear
                      : '') +
                    ' ITR Economics' +
                    document.getElementById('tradeMark').innerHTML,
                  fontSize: 8,
                },
                {
                  text:
                    this.apiResponse.CoverPage.ProductType +
                    document.getElementById('tradeMark').innerHTML,
                  fontSize: 8,
                  alignment: 'right',
                },
              ],
            };
          },
          styles: {
            blueBar: {
              background: '#3075A4',
              color: '#3075A4',
            },
            blueBarWText: {
              background: '#3075A4',
              color: '#FFF',
            },
            phaseA: {
              color: '#0A42D3',
              bold: true,
            },
            phaseB: {
              color: '#44AC4E',
              bold: true,
            },
            phaseC: {
              color: '#FF9916',
              bold: true,
            },
            phaseD: {
              color: '#D2232A',
              bold: true,
            },
            defaultTableTextColor: {
              color: '#A7A9AB',
              bold: true,
            },
            darkBlue: {
              color: '#2c4a6f',
            },
          },
          images: {
            pdfphaseAFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/pdfPhaseA.png',
            pdfphaseBFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/pdfPhaseB.png',
            pdfphaseCFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/pdfPhaseC.png',
            pdfphaseDFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/pdfPhaseD.png',
            phaseAFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-a-frame.png',
            phaseBFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-b-frame.png',
            phaseCFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-c-frame.png',
            phaseDFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-d-frame.png',
            phaseNAFrame:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-a-frame.png',
            phaseAFull:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-a-full.png',
            phaseBFull:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-b-full.png',
            phaseCFull:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-c-full.png',
            phaseDFull:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/phase-d-full.png',
            guidePage:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/timing-model-guide.png',
            termMethodology:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/terminology-methodology.png',
            managementObjectives:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/management-objectives.png',
            itrLogo:
              window.location.protocol +
              '//' +
              window.location.host +
              '/images/logos/new/itr-corporate-logo-color-no-white-space.png',
          },
        };

        var pdf = pdfMake.createPdf(docDefinition);
        if (this.preview) {
          pdf.open();
        } else {
          pdf.download(this.companyName + '.pdf');
        }

        setTimeout(function () {
          $('#loadingFileModal').modal('hide');
        }, 5000);
      },
      async downloadPPT() {
        let pres = new pptxgen();
        var companyLogo = await this.getReportLogo();
        var companyName = this.apiResponse.CompanyName;

        let companyNameFontSize = 35;
        if (companyName.length >= 70) {
          companyNameFontSize = 10;
        } else if (companyName.length >= 40) {
          companyNameFontSize = 12;
        } else if (companyName.length >= 20) {
          companyNameFontSize = 22;
        } else if (companyName.length >= 15) {
          companyNameFontSize = 32;
        }

        for (let i = 0; i < this.apiResponse.RoCDivPages.length; ++i) {
          var pageTitleFontSize = 35;
          if (this.apiResponse.RoCDivPages[i].PageTitle.length >= 70) {
            pageTitleFontSize = 10;
          } else if (this.apiResponse.RoCDivPages[i].PageTitle.length >= 40) {
            pageTitleFontSize = 12;
          } else if (this.apiResponse.RoCDivPages[i].PageTitle.length >= 20) {
            pageTitleFontSize = 22;
          } else if (this.apiResponse.RoCDivPages[i].PageTitle.length >= 15) {
            pageTitleFontSize = 32;
          }
        }

        // this.apiResponse.IndicatorPages[i].ChartTitle
        for (let i = 0; i < this.apiResponse.IndicatorPages.length; ++i) {
          var chartTitleFontSize = 35;
          if (this.apiResponse.IndicatorPages[i].ChartTitle.length >= 70) {
            chartTitleFontSize = 10;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 40
          ) {
            chartTitleFontSize = 12;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 20
          ) {
            chartTitleFontSize = 22;
          } else if (
            this.apiResponse.IndicatorPages[i].ChartTitle.length >= 15
          ) {
            chartTitleFontSize = 32;
          }
        }

        {
          // Master Slide Start
          pres.defineSlideMaster({
            title: 'MASTER_SLIDE',
            background: { color: 'FFFFFF' },
            margin: '2',
            objects: [
              {
                image: {
                  w: '99%',
                  h: '100%',
                  y: '0%',
                  x: '1%',
                  data: this.getBase64Image(
                    document.getElementById('pptFooter')
                  ),
                },
              },
            ],
          });
        } // Master Slide End

        {
          // Cover Page Start
          let coverPageSlide = pres.addSlide();

          coverPageSlide.addImage({
            data: this.getBase64Image(
              document.getElementById('pptCoverBackground')
            ),
            h: '100%',
            w: '100%',
            y: '0%',
            x: '0%',
          });

          coverPageSlide.addImage({
            data: companyLogo,
            w: '25%',
            h: '25%',
            y: '5%',
            x: '50%',
            sizing: { type: 'cover' },
          });

          coverPageSlide.addText(companyName, {
            x: '33%',
            y: '40%',
            w: '65%',
            fontFace: 'Calibri',
            fontSize: companyNameFontSize,
            bold: true,
            color: '13355e',
            align: 'right',
          });

          coverPageSlide.addText(
            this.apiResponse.CoverPage.ProductType +
              document.getElementById('tradeMark').innerHTML,
            {
              x: '23%',
              y: '56%',
              fontFace: 'Calibri',
              fontSize: '35',
              bold: true,
              color: '13355e',
              align: 'right',
            }
          );

          coverPageSlide.addText(
            [
              {
                text: 'www.itrondemand.com',
                options: { hyperlink: { url: 'https://www.itrondemand.com' } },
              },
            ],
            {
              x: '56%',
              y: '70%',
              fontFace: 'Calibri',
              fontSize: '30',
              color: '13355e',
            }
          );

          coverPageSlide.addText('603.796.2500 • reports@itreconomics.com', {
            x: '45%',
            y: '91%',
            fontFace: 'Calibri',
            fontSize: '12',
            color: '13355e',
            align: 'center',
          });

          coverPageSlide.addText(
            '©' +
              ' ' +
              this.currentYear +
              ' ' +
              'ITR Economics' +
              document.getElementById('tradeMark').innerHTML +
              ' ' +
              'All Rights Reserved',
            {
              x: '45%',
              y: '94%',
              fontFace: 'Calibri',
              fontSize: '12',
              color: '13355e',
              align: 'center',
            }
          );
        } // Cover Page End

        {
          // Your Business Title Start
          let businessTitleSlide = pres.addSlide();

          businessTitleSlide.addImage({
            data: this.getBase64Image(
              document.getElementById('pptBusinessBackground')
            ),
            h: '100%',
            w: '100%',
            y: '0%',
            x: '0%',
          });

          businessTitleSlide.addImage({
            data: companyLogo,
            w: '30%',
            h: '25%',
            y: '5%',
            x: '50%',
          });
        } // Your Business Title End

        {
          // Data Trends Start
          let dataTrendsSlide = pres.addSlide({ masterName: 'MASTER_SLIDE' });

          dataTrendsSlide.addText(
            this.apiResponse.DataTrendsPage.CompanyName +
              ' - ' +
              this.apiResponse.DataTrendsPage.DataType,
            {
              w: '100%',
              align: 'center',
              fontSize: 10,
              fontFace: 'Calibri',
              color: '386092',
              bold: true,
              y: '5%',
            }
          );

          dataTrendsSlide.addText(
            'Data Trends, ' + this.apiResponse.DataTrendsPage.DataLabel,
            {
              w: '100%',
              align: 'center',
              fontSize: '10',
              fontFace: 'Calibri',
              y: '10%',
            }
          );

          dataTrendsSlide.addImage({
            data: $('#dataTrendsChartImg').attr('src'),
            h: '70%',
            w: '70%',
            y: '15%',
            x: '15%',
          });
        } // Data Trends End

        {
          // ROC Start
          let rocSlide = pres.addSlide({ masterName: 'MASTER_SLIDE' });

          rocSlide.addText(
            this.apiResponse.CompanyName +
              ' - ' +
              this.apiResponse.RoCPage.DataType,
            {
              w: '100%',
              align: 'center',
              fontSize: 10,
              fontFace: 'Calibri',
              color: '386092',
              bold: true,
              y: '5%',
            }
          );

          rocSlide.addText('Rates-of-Change', {
            w: '100%',
            align: 'center',
            fontSize: '10',
            fontFace: 'Calibri',
            y: '10%',
          });

          rocSlide.addImage({
            data: $('#rocChartImg').attr('src'),
            h: '70%',
            w: '70%',
            align: 'center',
            y: '15%',
            x: '15%',
          });
        } // ROC End

        {
          // ROC Div Start

          if (this.apiResponse.RoCDivPages) {
            for (let i = 0; i < this.apiResponse.RoCDivPages.length; ++i) {
              let divisionComparisonSlide = pres.addSlide({
                masterName: 'MASTER_SLIDE',
              });

              divisionComparisonSlide.addText(
                this.apiResponse.RoCDivPages[i].PageTitle,
                {
                  w: '100%',
                  align: 'center',
                  fontSize: pageTitleFontSize,
                  fontFace: 'Calibri',
                  color: '386092',
                  bold: true,
                  y: '5%',
                }
              );

              // divisionComparisonSlide.addText( this.apiResponse.RoCDivPages[i].SubjectLabel, { w: '100%', align: 'center', fontSize: divTitleFontSize, fontFace: 'Calibri', y: '10%' });

              let division_img = $('#divisionChart' + i + 'ChartImg').attr(
                'src'
              );
              divisionComparisonSlide.addImage({
                data: division_img,
                h: '70%',
                w: '70%',
                align: 'center',
                y: '15%',
                x: '15%',
              });
            }
          }
        } // ROC Div End

        {
          // Leading Indicators Title Start
          let indicatorsTitleSlide = pres.addSlide();

          indicatorsTitleSlide.addImage({
            data: this.getBase64Image(
              document.getElementById('pptLeadingIndicatorsBackground')
            ),
            h: '100%',
            w: '100%',
            y: '0%',
            x: '0%',
          });

          indicatorsTitleSlide.addImage({
            data: companyLogo,
            w: '30%',
            h: '30%',
            y: '10%',
            x: '50%',
          });
        } // Leading Indicators Title End

        {
          // Indicator Phases Start
          let indicatorPhasesSlide = pres.addSlide({
            masterName: 'MASTER_SLIDE',
          });

          indicatorPhasesSlide.addText(
            'Indicator Signals For ' + this.companyName,
            {
              w: '100%',
              align: 'center',
              fontSize: 10,
              fontFace: 'Calibri',
              color: '386192',
              bold: true,
              y: '5%',
            }
          );

          indicatorPhasesSlide.addImage({
            data: companyLogo,
            w: '10%',
            h: '8%',
            y: '2%',
            x: '89%',
          });

          let phaseTableHeaders = [];
          var columnsSizes = [];
          for (
            let i = 0;
            i <
            this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Headers
              .length;
            ++i
          ) {
            let row = {
              text: this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                .Headers[i],
              options: {
                align: i === 0 ? 'left' : 'center',
                bold: true,
                fontSize: 8,
              },
            };

            if (i === 0) {
              columnsSizes.push(3);
            } else {
              columnsSizes.push(0.5);
            }

            phaseTableHeaders.push(row);
          }

          let rows = [];

          rows.push(phaseTableHeaders);

          //add row data
          for (
            let i = 0;
            i <
            this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Rows
              .length;
            ++i
          ) {
            var row = [
              {
                text: this.apiResponse.IndicatorSignalsPage
                  .IndicatorSignalsTable.Rows[i].SeriesName,
                options: {
                  align: 'left',
                  fill: i % 2 == 0 ? 'f2f2f2' : '',
                  fontSize: 8,
                },
              },
            ];
            for (
              let x = 0;
              x <
              this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Rows[
                i
              ].PhaseList.length;
              ++x
            ) {
              const phase =
                this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                  .Rows[i].PhaseList[x];
              row.push({
                text: phase,
                options: {
                  align: 'center',
                  color:
                    phase === 'A'
                      ? '0A42D3'
                      : phase === 'B'
                        ? '44AC4E'
                        : phase === 'C'
                          ? 'FF9916'
                          : phase === 'D'
                            ? 'D2232A'
                            : 'A7A9AB',
                  bold: true,
                  fontSize: 8,
                  fill: i % 2 == 0 ? 'f2f2f2' : '',
                },
              });
            }
            row.push({
              text: this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                .Rows[i].Timing,
              options: {
                align: 'center',
                color: 'A7A9AB',
                bold: true,
                fontSize: 8,
                fill: i % 2 == 0 ? 'f2f2f2' : '',
              },
            });

            rows.push(row);
          }

          var phaseTablePosition =
            this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable.Headers
              .length <= 3
              ? '28%'
              : this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                    .Headers.length <= 4
                ? '24%'
                : this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                      .Headers.length <= 5
                  ? '22%'
                  : this.apiResponse.IndicatorSignalsPage.IndicatorSignalsTable
                        .Headers.length <= 6
                    ? '20%'
                    : this.apiResponse.IndicatorSignalsPage
                          .IndicatorSignalsTable.Headers.length <= 7
                      ? '18%'
                      : this.apiResponse.IndicatorSignalsPage
                            .IndicatorSignalsTable.Headers.length <= 8
                        ? '15%'
                        : this.apiResponse.IndicatorSignalsPage
                              .IndicatorSignalsTable.Headers.length <= 9
                          ? '11%'
                          : this.apiResponse.IndicatorSignalsPage
                                .IndicatorSignalsTable.Headers.length <= 10
                            ? '12%'
                            : '0%';

          indicatorPhasesSlide.addTable(rows, {
            x: phaseTablePosition,
            y: '12%',
            colW: columnsSizes,
            align: 'center',
            border: { type: 'none' },
            autoPage: 'true',
          });
        } // Indicator Phases End

        {
          // Leading Indicators Start
          for (let i = 0; i < this.apiResponse.IndicatorPages.length; ++i) {
            let leadingIndicatorSlide = pres.addSlide({
              masterName: 'MASTER_SLIDE',
            });

            leadingIndicatorSlide.addText(
              this.apiResponse.IndicatorPages[i].Headline,
              {
                w: '100%',
                align: 'center',
                fontSize: '16',
                fontFace: 'Calibri',
                color: '386092',
                bold: true,
                y: '5%',
              }
            );

            leadingIndicatorSlide.addText(
              this.apiResponse.IndicatorPages[i].ChartTitle,
              {
                w: '100%',
                align: 'center',
                fontSize: chartTitleFontSize,
                fontFace: 'Calibri',
                y: '10%',
              }
            );

            let indicator_img = $(
              '#indicatorCompareChart' + i + 'ChartImg'
            ).attr('src');
            leadingIndicatorSlide.addImage({
              data: indicator_img,
              h: '70%',
              w: '70%',
              align: 'center',
              y: '15%',
              x: '15%',
            });
          }
        } // Leading Indicators End

        pres.writeFile(this.companyName + '.pptx').then(() => {
          $('#loadingFileModal').modal('hide');
        });
      },
      getBase64Image: function (img) {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);
        var dataURL = canvas.toDataURL('image/png');
        // return dataURL.replace(/^data:image\/(png|jpg);base64,/, "");
        return dataURL;
      },
      async createCoverPageChart() {
        let trace = {
          type: 'scatter',
          mode: 'scatter',
          x: this.apiResponse.CoverPage.MonthYear,
          y: this.apiResponse.CoverPage.HistoricData,
          line: {
            width: 4,
            color: '#183961',
          },
          yaxis: 'y1',
          xaxis: 'x',
          visible: true,
        };

        let fcLMRTrace = {
          mode: 'lines',
          x: this.apiResponse.CoverPage.MonthYear,
          y: this.apiResponse.CoverPage.LMRForecast,
          line: {
            width: 4,
            color: '#183961',
            dash: 'dashdot',
          },
          yaxis: 'y1',
          xaxis: 'x',
          visible: false,
        };

        let fcUMRTrace = {
          mode: 'lines',
          x: this.apiResponse.CoverPage.MonthYear,
          y: this.apiResponse.CoverPage.UMRForecast,
          line: {
            width: 4,
            color: '#183961',
            dash: 'dashdot',
          },
          yaxis: 'y1',
          xaxis: 'x',
          visible: false,
        };

        for (let i = 0; i < this.apiResponse.CoverPage.MonthYear.length; i++) {
          // const element = this.apiResponse.CoverPage.MonthYear[i];

          if (this.apiResponse.CoverPage.LMRForecast) {
            fcLMRTrace.visible = true;
          }

          if (this.apiResponse.CoverPage.UMRForecast) {
            fcUMRTrace.visible = true;
          }
        }

        //array of all traces to plot
        let chartData = [trace, fcLMRTrace, fcUMRTrace];

        //layout options
        const layout = {
          showlegend: false,
          autosize: true,
          dragmode: 'zoom',
          yaxis: {
            anchor: 'x',
            family: '\\"Open Sans\\", sans-serif',
            size: 12,
            color: '#444',
            automargin: true,
            showgrid: false,
            showline: false,
            showticklabels: false,
          },

          xaxis: {
            dtick: 'M24',
            automargin: true,
            showgrid: false,
            showline: false,
            showticklabels: false,
          },
          family: '\\"Open Sans\\", sans-serif',
          size: 12,
          color: '#444',
          height: 650,
          margin: {
            t: 50,
            l: 50,
            r: 50,
            b: 50,
          },
        };

        await Plotly.newPlot('coverPageChart', chartData, layout, {
          responsive: true,
          displaylogo: false,
          modeBarButtonsToRemove: [
            'autoScale2d',
            'toImage',
            'sendDataToCloud',
            'lasso2d',
            'toggleSpikelines',
            'resetScale2d',
          ],
          doubleClick: false /*'reset'*/,
        });
      },
    },
  };
</script>
